import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Fade,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Pagination,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ChevronRight } from "@mui/icons-material";
import { Search } from "lucide-react";
import { PageHeader } from "../../shared/components/PageHeader";
import { EmptyState } from "../../shared/components/EmptyState";
import { SortableHeader } from "../../shared/components/SortableHeader";
import { MobileSortControl } from "../../shared/components/MobileSortControl";
import { getLicenseExpiryStatus } from "../../utils/licenseStatus";

const ROWS_PER_PAGE = 10;

const toneSx = (tone, theme) => {
  if (tone === "success") {
    return { bgcolor: alpha(theme.palette.success.main, 0.1), color: "success.main" };
  }
  if (tone === "error") {
    return { bgcolor: alpha(theme.palette.error.main, 0.1), color: "error.main" };
  }
  return { bgcolor: alpha(theme.palette.warning.main, 0.1), color: "warning.main" };
};

// Ranks worst-first: an expired license outranks an expiring one, which outranks an offline camera.
function getProjectHealth(project) {
  const expiringCount = project.licenses.filter(
    (l) => getLicenseExpiryStatus(l.end_date) === "expiring"
  ).length;

  if (project.expired_licenses > 0) {
    return { label: `${project.expired_licenses} expired`, severity: 3, tone: "error" };
  }
  if (expiringCount > 0) {
    return { label: `${expiringCount} expiring`, severity: 2, tone: "warning" };
  }
  if (project.offline_cameras > 0) {
    return { label: `${project.offline_cameras} offline`, severity: 1, tone: "warning" };
  }
  return { label: "Healthy", severity: 0, tone: "success" };
}

function sortRows(rows, sortConfig) {
  const dir = sortConfig.direction === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    let aValue;
    let bValue;
    switch (sortConfig.key) {
      case "systems":
        aValue = a.systems.length;
        bValue = b.systems.length;
        break;
      case "cameras":
        aValue = a.total_cameras;
        bValue = b.total_cameras;
        break;
      case "licenses":
        aValue = a.total_licenses;
        bValue = b.total_licenses;
        break;
      case "health":
        aValue = a.health.severity;
        bValue = b.health.severity;
        break;
      default:
        aValue = (a.project_id || "").toLowerCase();
        bValue = (b.project_id || "").toLowerCase();
    }
    if (aValue < bValue) return -1 * dir;
    if (aValue > bValue) return 1 * dir;
    return 0;
  });
}

function ProjectsView({
  darkMode,
  theme,
  projects,
  searchTerm,
  handleSearch,
  loading,
  isMobile,
  setSelectedProject,
  setActiveView,
}) {
  const [sortConfig, setSortConfig] = useState({ key: "project_id", direction: "asc" });
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const handleSort = (_tableType, key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handlePageChange = (event, value) => setPage(value);

  const openProject = (projectId) => {
    setSelectedProject(projectId);
    setActiveView("projectDetails");
  };

  const rows = projects
    .filter(
      (project) =>
        project.project_id && project.project_id.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .map((project) => ({
      ...project,
      amcSystems: project.systems.filter((s) => s.amc).length,
      health: getProjectHealth(project),
    }));

  const sortedRows = sortRows(rows, sortConfig);
  const paginatedRows = sortedRows.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE);

  return (
    <Fade in={true} timeout={500}>
      <Box>
        <PageHeader
          title="Project Management"
          subtitle="View and manage all projects"
          darkMode={darkMode}
          actions={
            <TextField
              variant="outlined"
              placeholder="Search projects..."
              size="small"
              value={searchTerm}
              onChange={handleSearch}
              sx={{ width: { xs: "100%", sm: 300 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} />
                  </InputAdornment>
                ),
              }}
            />
          }
        />

        {loading.projects ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 8 }}>
            <CircularProgress />
          </Box>
        ) : isMobile ? (
          <Card sx={{ bgcolor: darkMode ? "#0E1626" : "background.paper" }}>
            <CardContent sx={{ p: 2 }}>
              <MobileSortControl
                tableType="projects"
                currentConfig={sortConfig}
                handleSort={handleSort}
                options={[
                  { value: 'project_id', label: 'Project' },
                  { value: 'systems', label: 'Systems' },
                  { value: 'cameras', label: 'Cameras' },
                  { value: 'licenses', label: 'Licenses' },
                  { value: 'health', label: 'Health' },
                ]}
              />
              {paginatedRows.map((project) => (
                <Box
                  key={project.project_id}
                  onClick={() => openProject(project.project_id)}
                  sx={{
                    p: 2,
                    mb: 1.5,
                    borderRadius: 2,
                    cursor: "pointer",
                    bgcolor: darkMode ? "#16213A" : "#EAF3FB",
                    border: darkMode ? "1px solid #24304A" : "1px solid #D8E1E8",
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: darkMode ? "#EAF3FB" : "#0E1B2C" }}>
                      {project.project_id}
                    </Typography>
                    <Chip
                      size="small"
                      label={project.health.label}
                      sx={{ fontWeight: 600, ...toneSx(project.health.tone, theme) }}
                    />
                  </Box>
                  <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 1.5 }}>
                    <Box>
                      <Typography variant="caption" sx={{ display: "block", color: darkMode ? "#93A2BC" : "text.secondary" }}>
                        Systems
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: darkMode ? "#EAF3FB" : "#0E1B2C" }}>
                        {project.systems.length}
                        <Typography component="span" variant="caption" sx={{ color: darkMode ? "#93A2BC" : "text.secondary", ml: 0.5 }}>
                          ({project.amcSystems} AMC)
                        </Typography>
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ display: "block", color: darkMode ? "#93A2BC" : "text.secondary" }}>
                        Cameras
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: darkMode ? "#EAF3FB" : "#0E1B2C" }}>
                        {project.total_cameras}
                        <Typography component="span" variant="caption" sx={{ color: darkMode ? "#93A2BC" : "text.secondary", ml: 0.5 }}>
                          ({project.online_cameras} online)
                        </Typography>
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ display: "block", color: darkMode ? "#93A2BC" : "text.secondary" }}>
                        Licenses
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: darkMode ? "#EAF3FB" : "#0E1B2C" }}>
                        {project.total_licenses}
                        <Typography component="span" variant="caption" sx={{ color: darkMode ? "#93A2BC" : "text.secondary", ml: 0.5 }}>
                          ({project.active_licenses} active)
                        </Typography>
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <IconButton
                      size="small"
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.2) },
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        openProject(project.project_id);
                      }}
                    >
                      <ChevronRight fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              ))}
              {sortedRows.length === 0 && (
                <EmptyState
                  darkMode={darkMode}
                  title="No projects found"
                  subtitle="Projects will appear here when systems are added with project IDs"
                />
              )}
              {sortedRows.length > ROWS_PER_PAGE && (
                <Box sx={{ display: "flex", justifyContent: "center", pt: 2 }}>
                  <Pagination
                    count={Math.ceil(sortedRows.length / ROWS_PER_PAGE)}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    size="medium"
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card sx={{ bgcolor: darkMode ? "#0E1626" : "background.paper" }}>
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader sx={{ minWidth: 760 }}>
                <TableHead>
                  <TableRow>
                    <SortableHeader label="Project" tableType="projects" columnKey="project_id" currentConfig={sortConfig} handleSort={handleSort} />
                    <SortableHeader label="Systems" tableType="projects" columnKey="systems" currentConfig={sortConfig} handleSort={handleSort} align="right" />
                    <SortableHeader label="Cameras" tableType="projects" columnKey="cameras" currentConfig={sortConfig} handleSort={handleSort} align="right" />
                    <SortableHeader label="Licenses" tableType="projects" columnKey="licenses" currentConfig={sortConfig} handleSort={handleSort} align="right" />
                    <SortableHeader label="Health" tableType="projects" columnKey="health" currentConfig={sortConfig} handleSort={handleSort} />
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedRows.map((project) => (
                    <TableRow
                      key={project.project_id}
                      hover
                      onClick={() => openProject(project.project_id)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: darkMode ? "#EAF3FB" : "#0E1B2C" }}>
                          {project.project_id}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ color: darkMode ? "#EAF3FB" : "#0E1B2C" }}>
                          {project.systems.length}
                        </Typography>
                        <Typography variant="caption" sx={{ color: darkMode ? "#93A2BC" : "text.secondary" }}>
                          {project.amcSystems} with AMC
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ color: darkMode ? "#EAF3FB" : "#0E1B2C" }}>
                          {project.total_cameras}
                        </Typography>
                        <Typography variant="caption" sx={{ color: darkMode ? "#93A2BC" : "text.secondary" }}>
                          {project.online_cameras} online
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ color: darkMode ? "#EAF3FB" : "#0E1B2C" }}>
                          {project.total_licenses}
                        </Typography>
                        <Typography variant="caption" sx={{ color: darkMode ? "#93A2BC" : "text.secondary" }}>
                          {project.active_licenses} active
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={project.health.label}
                          sx={{ fontWeight: 600, ...toneSx(project.health.tone, theme) }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          sx={{
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.2) },
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            openProject(project.project_id);
                          }}
                        >
                          <ChevronRight fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {sortedRows.length > ROWS_PER_PAGE && (
              <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                <Pagination
                  count={Math.ceil(sortedRows.length / ROWS_PER_PAGE)}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  size="large"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}

            {sortedRows.length === 0 && (
              <EmptyState
                darkMode={darkMode}
                title="No projects found"
                subtitle="Projects will appear here when systems are added with project IDs"
              />
            )}
          </Card>
        )}
      </Box>
    </Fade>
  );
}

export default ProjectsView;
